'use client'

import { useState, useEffect, useCallback } from 'react'
import { apiFetch } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Users,
  ClipboardList,
  CalendarCheck,
  LogOut,
  Menu,
  Home,
  FileText,
  Plane,
  Home as HomeIcon,
  Settings,
  Bell,
  ChevronsLeft,
  ChevronsRight,
  Clock,
  AlertCircle,
  BarChart3,
  IndianRupee,
  Trophy,
  Sparkles,
  FolderKanban,
  LogIn,
  CalendarDays,
} from 'lucide-react'
import { UserManagement } from './UserManagement'
import { ProjectManagement } from './ProjectManagement'
import { TaskManagement } from './TaskManagement'
import { AttendanceView } from './AttendanceView'
import { LeaveManagement } from './LeaveManagement'
import { WFHManagement } from './WFHManagement'
import { DailyWorkView } from './DailyWorkView'
import { PerformanceAnalytics } from './PerformanceAnalytics'
import { MonthlyReports } from './MonthlyReports'
import { SettingsView } from './SettingsView'
import { AdminSelfAttendance } from './AdminSelfAttendance'
import { CalendarWorkspace } from './CalendarWorkspace'
import { withErrorBoundary } from '@/components/ErrorBoundary'
import { User } from '@/types/user'
import { GlassCard } from '@/components/ui/glass-card'
import { KpiTile } from '@/components/ui/kpi-tile'
import { PageShell } from '@/components/ui/page-shell'
import { SectionHeader } from '@/components/ui/section-header'
import { StatusPill } from '@/components/ui/status-pill'
import { cn, formatTime } from '@/lib/utils'

interface DashboardStats {
  totalEmployees: number
  activeProjects: number
  pendingLeaves: number
  pendingWFH: number
  todayAttendance: number
  pendingTasks: number
  yesterdayAttendance: number
  yesterdayCompletedTasks: number
  overdueTasksCount: number
}

interface InternReport {
  id: string
  userName: string
  notes: string
  hours: number
  projects: string[]
}

interface OverdueTask {
  id: string
  title: string
  dueDate: string
  priority: string
  status: string
  assignees: string[]
}

interface BestIntern {
  id: string
  name: string
  score: number
}

interface AdminNotification {
  id: string
  title: string
  message: string
  type: string
  isRead: boolean
  link?: string
  createdAt: string
}

interface DashboardData {
  stats: DashboardStats
  yesterdaySummary: {
    attendance: number
    completedTasks: number
    internReports: InternReport[]
  }
  overdueTasks: OverdueTask[]
  bestInterns: BestIntern[]
  todayRoster?: RosterEntry[]
  presentToday?: RosterEntry[]
}

type Presence = 'IN' | 'OUT' | 'WFH' | 'ON_LEAVE' | 'NOT_CHECKED_IN'

interface RosterEntry {
  userId: string
  name: string
  employeeId: string | null
  department: string | null
  date: string
  checkIn: string | null
  checkOut: string | null
  workMode: string
  status: string | null
  role?: string | null
  presence: Presence
}

// Label + tone for each roll-call state. 'WFH'/'ON_LEAVE' mean approved for
// today but not checked in — expected elsewhere rather than unaccounted for.
const PRESENCE_META: Record<Presence, { label: string; tone: 'green' | 'neutral' | 'blue' | 'violet' | 'red' }> = {
  IN: { label: 'In', tone: 'green' },
  OUT: { label: 'Out', tone: 'neutral' },
  WFH: { label: 'WFH', tone: 'blue' },
  ON_LEAVE: { label: 'On leave', tone: 'violet' },
  NOT_CHECKED_IN: { label: 'Not in', tone: 'red' },
}

const presenceMeta = (p: Presence) => PRESENCE_META[p] ?? PRESENCE_META.NOT_CHECKED_IN

interface AdminDashboardProps {
  user: User
  onLogout: () => void
}

const MENU_ITEMS = [
  { id: 'overview', label: 'Overview', icon: Home },
  { id: 'attendance', label: 'Attendance', icon: CalendarCheck },
  { id: 'calendar', label: 'Calendar', icon: CalendarDays },
  { id: 'users', label: 'Employees', icon: Users },
  { id: 'tasks', label: 'Tasks', icon: ClipboardList },
  { id: 'leaves', label: 'Leave', icon: Plane },
  { id: 'wfh', label: 'WFH', icon: HomeIcon },
  { id: 'daily-work', label: 'Daily Work', icon: FileText },
  { id: 'stipends', label: 'Stipends', icon: IndianRupee },
  { id: 'performance', label: 'Performance', icon: BarChart3 },
] as const

export function AdminDashboard({ user, onLogout }: AdminDashboardProps) {
  const [activeTab, setActiveTab] = useState<string>('overview')
  const [mobileOpen, setMobileOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [notifications, setNotifications] = useState<AdminNotification[]>([])
  const [presentOpen, setPresentOpen] = useState(false)

  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0]

  const allowedMenuItems = MENU_ITEMS

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await apiFetch('/api/dashboard')
      const json = await res.json()
      setData(json)
    } catch (error) {
      console.error('Error fetching dashboard:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await apiFetch('/api/notifications')
      const json = await res.json()
      setNotifications((json.notifications || []).filter((n: AdminNotification) => !n.isRead))
    } catch (error) {
      console.error('Error fetching notifications:', error)
    }
  }, [])

  useEffect(() => {
    fetchDashboard()
    fetchNotifications()
    // Leave and WFH requests arrive while the dashboard is open, so poll.
    const t = setInterval(fetchNotifications, 60000)
    return () => clearInterval(t)
  }, [fetchDashboard, fetchNotifications])

  // Open the tab a notification points at, and mark it read.
  const openNotification = async (notif: AdminNotification) => {
    const tab = (notif.link || '').replace(/^\//, '')
    if (tab && MENU_ITEMS.some(m => m.id === tab)) setActiveTab(tab)
    setNotifications(prev => prev.filter(n => n.id !== notif.id))
    try {
      await apiFetch('/api/notifications', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: [notif.id] }),
      })
    } catch (error) {
      console.error('Error marking notification read:', error)
    }
  }

  const DailyWorkViewWithErrorBoundary = withErrorBoundary(DailyWorkView)
  const TaskManagementWithErrorBoundary = withErrorBoundary(TaskManagement)

  const activeMeta = MENU_ITEMS.find(m => m.id === activeTab)

  const handleNav = (id: string) => {
    setActiveTab(id)
    setMobileOpen(false)
  }

  return (
    <div className="min-h-screen text-neutral-800 dark:text-white/80">
      {/* Mobile overlay */}
      {mobileOpen && (
        <button
          aria-label="Close menu"
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-40 flex flex-col border-r border-white/10 bg-black text-white transition-all duration-300',
            'md:static md:translate-x-0',
            mobileOpen ? 'translate-x-0' : '-translate-x-full',
            collapsed ? 'md:w-[76px]' : 'md:w-64',
            'w-72'
          )}
        >
          {/* px-3 (not px-4): the collapsed rail is 76px, so the padding is what
              caps how large the logo box can grow without clipping. */}
          <div className="flex items-center gap-3 px-3 py-4 border-b border-white/10">
            <div className="shrink-0 rounded-xl bg-white p-1 shadow-md">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg">
                <img src="/sasha-logo-small.png" alt="Sasha Infinity" className="h-8 w-auto" />
              </div>
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <h1 className="text-sm font-semibold tracking-tight text-white">Sasha Infinity</h1>
                <p className="text-[11px] text-white/40">Management</p>
              </div>
            )}
          </div>

          <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
            {allowedMenuItems.map(item => {
              const active = activeTab === item.id
              return (
                <button
                  key={item.id}
                  onClick={() => handleNav(item.id)}
                  className={cn(
                    'group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/50',
                    active
                      ? 'bg-gradient-to-r from-orange-500/25 to-orange-500/5 text-orange-200 ring-1 ring-inset ring-orange-500/30'
                      : 'text-white/70 hover:text-white hover:bg-white/10',
                    collapsed && 'md:justify-center md:px-2'
                  )}
                  title={collapsed ? item.label : undefined}
                >
                  <item.icon className={cn('h-4.5 w-4.5 shrink-0', active && 'text-orange-300')} />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                  {active && !collapsed && (
                    <span className="ml-auto h-1.5 w-1.5 rounded-full bg-orange-400 shadow-[0_0_8px_2px_rgba(251,146,60,0.6)]" />
                  )}
                </button>
              )
            })}
          </nav>

          {/* Profile footer */}
          <div className="mt-auto border-t border-white/10 p-3">
            {collapsed ? (
              <div className="hidden md:flex flex-col items-center gap-2">
                <Avatar className="h-9 w-9 bg-gradient-to-br from-orange-500 to-orange-600" title={user?.name}>
                  <AvatarFallback className="bg-transparent text-white text-sm font-semibold">
                    {user.name?.charAt(0).toUpperCase() || 'A'}
                  </AvatarFallback>
                </Avatar>
                 <button
                  onClick={() => setActiveTab('settings')}
                  title="Settings"
                  aria-label="Settings"
                  className={cn(
                    'rounded-lg p-2 transition-colors',
                    activeTab === 'settings'
                      ? 'text-orange-400 bg-white/10 ring-1 ring-inset ring-orange-500/30'
                      : 'text-white/60 hover:text-white hover:bg-white/10'
                  )}
                >
                  <Settings className="h-4 w-4" />
                </button>
                <button onClick={onLogout} title="Logout" aria-label="Logout" className="rounded-lg p-2 text-white/60 hover:text-rose-300 hover:bg-white/10 transition-colors">
                  <LogOut className="h-4 w-4" />
                </button>
                <button onClick={() => setCollapsed(false)} title="Expand" aria-label="Expand sidebar" className="rounded-lg p-2 text-white/50 hover:text-white hover:bg-white/10 transition-colors">
                  <ChevronsRight className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-3 rounded-xl bg-white/5 px-3 py-2.5 ring-1 ring-inset ring-white/10">
                  <Avatar className="h-9 w-9 shrink-0 bg-gradient-to-br from-orange-500 to-orange-600">
                    <AvatarFallback className="bg-transparent text-white text-sm font-semibold">
                      {user.name?.charAt(0).toUpperCase() || 'A'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white truncate">{user?.name || 'User'}</p>
                    <p className="text-[11px] text-white/50 truncate">{user.role === 'ADMIN' ? 'Administrator' : user.role === 'HR' ? 'HR Manager' : 'Employee'}</p>
                  </div>
                  <button
                    onClick={() => setActiveTab('settings')}
                    title="Settings"
                    aria-label="Settings"
                    className={cn(
                      'shrink-0 rounded-lg p-2 transition-colors',
                      activeTab === 'settings'
                        ? 'text-orange-400 bg-white/10 ring-1 ring-inset ring-orange-500/30'
                        : 'text-white/60 hover:text-white hover:bg-white/10'
                    )}
                  >
                    <Settings className="h-4 w-4" />
                  </button>
                </div>
                <button
                  onClick={onLogout}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <LogOut className="h-4 w-4" /> Logout
                </button>
                <button
                  onClick={() => setCollapsed(true)}
                  className="hidden md:flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <ChevronsLeft className="h-4 w-4" /> Collapse
                </button>
              </div>
            )}
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top bar */}
          <header className="sticky top-0 z-20 border-b border-neutral-200 bg-white shadow-sm dark:border-white/10 dark:bg-neutral-950/80 dark:backdrop-blur-xl dark:shadow-none">
            <div className="flex items-center justify-between gap-3 px-3 md:px-6 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden text-neutral-600 dark:text-white/60 hover:text-white hover:bg-neutral-50 dark:bg-transparent dark:hover:bg-white/5"
                  onClick={() => setMobileOpen(true)}
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
                <div className="min-w-0">
                  <p className="text-[11px] md:text-xs text-neutral-400 dark:text-white/40 truncate">
                    Welcome back, {user?.name || 'User'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1 md:gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="text-neutral-600 dark:text-white/60 hover:text-white hover:bg-neutral-50 dark:bg-transparent dark:hover:bg-white/5 relative">
                      <Bell className="h-4.5 w-4.5" />
                      {notifications.length > 0 && (
                        <span className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-orange-500 text-[10px] flex items-center justify-center text-white font-medium">
                          {notifications.length}
                        </span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72 sm:w-80 bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-white/10">
                    <DropdownMenuLabel>System Notifications</DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-white/10" />
                    {notifications.length === 0 ? (
                      <div className="p-4 text-xs text-center text-neutral-500 dark:text-white/50">
                        No new notifications
                      </div>
                    ) : (
                      notifications.slice(0, 6).map((notif) => (
                        <DropdownMenuItem
                          key={notif.id}
                          className="cursor-pointer"
                          onClick={() => openNotification(notif)}
                        >
                          <div className="flex flex-col gap-1">
                            <span className="font-medium text-sm">{notif.title}</span>
                            <span className="text-xs text-neutral-500 dark:text-white/50">{notif.message}</span>
                          </div>
                        </DropdownMenuItem>
                      ))
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          <main className="flex-1 overflow-auto p-3 md:p-6">
            {activeTab === 'overview' && (
              <PageShell>
                <SectionHeader
                  icon={Home}
                  title="Overview"
                  description={`Snapshot for ${yesterday} and live activity`}
                />

                <div className="mb-6">
                  <AdminSelfAttendance userId={user.id} showHistory={false} />
                </div>

                 {/* KPI grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                  <KpiTile
                    label="Today Present"
                    value={data?.stats.todayAttendance ?? 0}
                    icon={CalendarCheck}
                    tone="green"
                    hint={`Yesterday: ${data?.stats.yesterdayAttendance ?? 0}`}
                    onClick={() => setPresentOpen(true)}
                  />
                  <KpiTile
                    label="Employees"
                    value={data?.stats.totalEmployees ?? 0}
                    icon={Users}
                    tone="violet"
                    onClick={() => setActiveTab('users')}
                  />
                  <KpiTile
                    label="Pending Leaves"
                    value={data?.stats.pendingLeaves ?? 0}
                    icon={Plane}
                    tone="orange"
                    onClick={() => setActiveTab('leaves')}
                  />
                  <KpiTile
                    label="Pending WFH"
                    value={data?.stats.pendingWFH ?? 0}
                    icon={HomeIcon}
                    tone="cyan"
                    onClick={() => setActiveTab('wfh')}
                  />
                  <KpiTile
                    label="Tasks Done Yesterday"
                    value={data?.stats.yesterdayCompletedTasks ?? 0}
                    icon={Sparkles}
                    tone="green"
                    onClick={() => setActiveTab('tasks')}
                  />
                </div>

                {/* Yesterday summary + best interns */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
                  <GlassCard tone="orange" className="lg:col-span-2">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="h-4 w-4 text-orange-300" />
                      <h3 className="text-sm font-semibold tracking-tight">Yesterday&apos;s summary</h3>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <SummaryStat label="Attendance" value={data?.stats.yesterdayAttendance ?? 0} accent="text-emerald-300" />
                      <SummaryStat label="Tasks done" value={data?.stats.yesterdayCompletedTasks ?? 0} accent="text-orange-300" />
                      <SummaryStat label="Reports filed" value={data?.yesterdaySummary.internReports.length ?? 0} accent="text-blue-300" />
                    </div>
                  </GlassCard>

                  <GlassCard tone="green" className="lg:col-span-1">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2">
                        <LogIn className="h-4 w-4 text-emerald-300" />
                        <h3 className="text-sm font-semibold tracking-tight">Today&apos;s check-ins</h3>
                      </div>
                      <button
                        type="button"
                        onClick={() => setActiveTab('attendance')}
                        className="text-[11px] text-neutral-500 dark:text-white/50 hover:text-orange-500 dark:hover:text-orange-400 transition-colors"
                      >
                        View all
                      </button>
                    </div>

                    {!data?.todayRoster?.length ? (
                      <div className="flex flex-col items-center justify-center py-8 gap-2">
                        <LogIn className="h-8 w-8 text-neutral-300 dark:text-white/20" />
                        <p className="text-xs text-neutral-400 dark:text-white/40">No employees to show</p>
                      </div>
                    ) : (
                      <>
                        <p className="mb-2 text-[11px] text-neutral-500 dark:text-white/50">
                          {data.todayRoster.filter(r => r.checkIn).length} of {data.todayRoster.length} checked in
                          {(() => {
                            const missing = data.todayRoster.filter(r => r.presence === 'NOT_CHECKED_IN').length
                            return missing > 0 ? ` · ${missing} not in` : ''
                          })()}
                        </p>
                        {/* Every employee, not a top-N preview — the list scrolls
                            so absences stay visible next to the check-ins. */}
                        <ul className="max-h-[320px] overflow-y-auto pr-1 space-y-2.5">
                          {data.todayRoster.map((c) => (
                            <li key={c.userId} className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-sm text-neutral-900 dark:text-white/90 truncate">{c.name}</p>
                                <p className="text-[11px] text-neutral-400 dark:text-white/40 truncate">
                                  {c.department || c.employeeId || c.workMode}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="text-sm tabular-nums text-neutral-800 dark:text-white/80">
                                  {c.checkIn ? formatTime(c.checkIn) : '—'}
                                </span>
                                <StatusPill tone={presenceMeta(c.presence).tone} dot>
                                  {presenceMeta(c.presence).label}
                                </StatusPill>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </GlassCard>
                </div>

              </PageShell>
            )}

             {activeTab === 'users' && <UserManagement />}
            {activeTab === 'tasks' && <TaskManagementWithErrorBoundary />}
            {activeTab === 'attendance' && <AttendanceView currentUserId={user.id} />}
            {activeTab === 'calendar' && <CalendarWorkspace userId={user.id} />}
            {activeTab === 'leaves' && <LeaveManagement />}
            {activeTab === 'wfh' && <WFHManagement />}
            {activeTab === 'daily-work' && <DailyWorkViewWithErrorBoundary />}
            {activeTab === 'stipends' && <MonthlyReports />}
            {activeTab === 'performance' && <PerformanceAnalytics />}
            {activeTab === 'settings' && <SettingsView />}
          </main>
        </div>
      </div>

      <Dialog open={presentOpen} onOpenChange={setPresentOpen}>
        <DialogContent className="bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-white/10 max-w-lg w-full text-neutral-900 dark:text-neutral-100">
          <DialogHeader>
            <DialogTitle className="text-neutral-900 dark:text-white/90">Present today</DialogTitle>
            <DialogDescription className="text-neutral-500 dark:text-white/50">
              {(data?.presentToday?.length ?? 0)} employee{(data?.presentToday?.length ?? 0) === 1 ? '' : 's'} checked in today
            </DialogDescription>
          </DialogHeader>

          {!data?.presentToday?.length ? (
            <div className="flex flex-col items-center justify-center gap-2 py-10">
              <CalendarCheck className="h-8 w-8 text-neutral-300 dark:text-white/20" />
              <p className="text-sm text-neutral-400 dark:text-white/40">Nobody has checked in yet today</p>
            </div>
          ) : (
            <ul className="max-h-[55vh] overflow-y-auto space-y-2 pr-1">
              {data.presentToday.map(person => (
                <li
                  key={person.userId}
                  className="flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 bg-neutral-50 dark:bg-white/[0.03] ring-1 ring-inset ring-neutral-200 dark:ring-white/10"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar className="h-8 w-8 shrink-0 bg-gradient-to-br from-orange-500 to-orange-600">
                      <AvatarFallback className="bg-transparent text-white text-xs font-semibold">
                        {person.name?.charAt(0).toUpperCase() || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-neutral-900 dark:text-white/90 truncate">{person.name}</p>
                      <p className="text-[11px] text-neutral-400 dark:text-white/40 truncate">
                        {[person.employeeId, person.department, person.workMode].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs tabular-nums text-neutral-600 dark:text-white/60">
                      {formatTime(person.checkIn)}
                      {person.checkOut ? ` → ${formatTime(person.checkOut)}` : ''}
                    </span>
                    <StatusPill tone={person.checkOut ? 'neutral' : 'green'} dot>
                      {person.checkOut ? 'Out' : 'In'}
                    </StatusPill>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              className="border-neutral-200 dark:border-white/10"
              onClick={() => { setPresentOpen(false); setActiveTab('attendance') }}
            >
              Open attendance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function SummaryStat({ label, value, accent }: { label: string; value: number; accent: string }) {
  return (
    <div className="space-y-1 px-2 first:pl-0 not-first:border-l border-neutral-200 dark:border-white/10">
      <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-white/40">{label}</p>
      <p className={cn('text-xl md:text-2xl font-semibold tabular-nums tracking-tight', accent)}>{value}</p>
    </div>
  )
}
